/**
 * ClarificationNeededCard - Displays clarification request from agent
 * 
 * Shows the question, missing information, and suggested responses.
 */
import { cn } from '@/lib/utils'
import { HelpCircle } from 'lucide-react'

export interface ClarificationNeededData {
  question: string
  intent_type?: string
  suggested_responses?: Array<string>
  missing_info?: Array<string>
}

export interface ClarificationNeededCardProps {
  data: ClarificationNeededData
  reasoning?: string
  className?: string
}

export function ClarificationNeededCard({ data, reasoning, className }: ClarificationNeededCardProps) {
  const { question, missing_info, suggested_responses } = data
  
  return (
    <div className={cn(
      "rounded-lg border-2 border-amber-300 dark:border-amber-600 bg-amber-50 dark:bg-amber-950/30 p-4 space-y-3",
      className
    )}>
      <div className="flex items-start gap-3">
        <div className="p-2 rounded-full bg-amber-100 dark:bg-amber-900/50">
          <HelpCircle className="h-5 w-5 text-amber-600 dark:text-amber-400" />
        </div>
        <div className="flex-1 min-w-0">
          <h3 className="font-semibold text-base text-amber-800 dark:text-amber-200">Clarification Needed</h3>
          <p className="text-sm mt-1 text-amber-900 dark:text-amber-100">{question}</p>
        </div>
      </div>
      
      {/* Missing info */}
      {missing_info && missing_info.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium text-amber-700 dark:text-amber-300">Missing Information</h4>
          <div className="flex flex-wrap gap-1">
            {missing_info.map((info, idx) => (
              <span 
                key={idx} 
                className="px-2 py-0.5 rounded bg-amber-200 dark:bg-amber-800 text-amber-800 dark:text-amber-200 text-xs"
              >
                {info.replace(/_/g, ' ')}
              </span>
            ))}
          </div>
        </div>
      )}
      
      {/* Suggested responses */}
      {suggested_responses && suggested_responses.length > 0 && (
        <div className="space-y-1">
          <h4 className="text-xs font-medium text-amber-700 dark:text-amber-300">Suggested Responses</h4>
          <ul className="space-y-1">
            {suggested_responses.map((response, idx) => (
              <li key={idx} className="text-sm text-amber-800 dark:text-amber-200">• {response}</li>
            ))}
          </ul>
        </div>
      )}
      
      {/* Reasoning */}
      {reasoning && (
        <div className="pt-2 border-t border-amber-300 dark:border-amber-700">
          <p className="text-xs text-amber-700 dark:text-amber-400">{reasoning}</p>
        </div>
      )}
    </div>
  )
}
